<template>
    <div id="app" class="l-wrapper" :class="className" :style="`--vh: ${W.h / 100}px;`">
        <app-header />
        <app-base :page="currentPage" />
        <transition name="fade">
            <light-box v-show="isLightBoxOpen" :is-open="isLightBoxOpen" />
        </transition>
        <app-cursor />
    </div>
</template>

<script>
import { mapState } from "vuex";

import "assets/scss/styles.scss";

import Variables from "src/mixins/variables";
import { EventBus } from "src/event-bus.js";

import AppBase from "layout/AppBase";
import AppCursor from "objects/AppCursor";
import AppHeader from "layout/AppHeader";
import LightBox from "components/LightBox/LightBox";

export default {
    name: "App",
    components: {
        AppBase,
        AppCursor,
        AppHeader,
        LightBox,
    },
    mixins: [Variables],
    data: () => ({
        title: null,
        desc: null,
        isLoading: true,
    }),
    metaInfo() {
        if (!this.currentPage) return null;

        const title = this.currentPage.data.meta_title
            ? `${this.currentPage.data.meta_title} | ${this.$t("meta.titleTemplate")}`
            : this.$t("meta.titleTemplate");
        const desc = this.currentPage.data.meta_desc || this.currentPage.data.meta_description || this.$t("meta.description");
        return {
            title: title,
            meta: [
                // Description
                {
                    name: "description",
                    content: desc,
                },

                // Author
                {
                    name: "author",
                    content: "MamboMambo",
                },

                // Facebook
                {
                    property: "og:title",
                    content: title,
                },
                {
                    property: "og:type",
                    content: "website",
                },
                {
                    property: "og:image",
                    content: `${process.env.VUE_APP_SITE_URL}/og/facebook.jpg`,
                },
                {
                    property: "og:image:type",
                    content: "image/jpeg",
                },
                {
                    property: "og:site_name",
                    content: title,
                },
                {
                    property: "og:description",
                    content: desc,
                },

                // Twitter
                {
                    name: "twitter:card",
                    content: "summary_large_image",
                },
                {
                    name: "twitter:site",
                    content: "@mois_sociale",
                },
                {
                    name: "twitter:creator",
                    content: "@allomambo",
                },
                {
                    name: "twitter:title",
                    content: title,
                },
                {
                    name: "twitter:description",
                    content: desc,
                },
                {
                    name: "twitter:image",
                    content: `${process.env.VUE_APP_SITE_URL}/og/twitter.jpg`,
                },
            ],
        };
    },
    created() {
        // Browser and OS classes
        document.documentElement.classList.add(`is-${this.browser.oser.replace(" ", "-").toLowerCase()}`);
        document.documentElement.classList.add(`is-${this.browser.name}`);
    },
    mounted() {
        // Loader animation start
        EventBus.$on("loader-start", () => {
            this.isLoading = true;
        });

        EventBus.$on("loader-complete", () => {
            this.isLoading = false;
        });
    },
    computed: {
        ...mapState({
            currentPage: (state) => state.global.currentPage,
            currentPageName: (state) => state.global.currentPageName,
            isFirstLoad: (state) => state.global.isFirstLoad,
            isLightBoxOpen: (state) => state.lightBox.isLightBoxOpen,
        }),
        className() {
            let classname = "l-wrapper";

            classname += ` p-${this.currentPageName}`;

            if (this.isLoading) {
                classname += " is-loading";
            }
            if (this.isFirstLoad) {
                classname += " first-load";
            }

            return classname;
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/scss/layout/_anims.scss";

#app {
    background-color: $color-lighter;
}
.first-load {
    background-color: $color-bright;
}
</style>
